import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { drawingNumberSearchVariants } from 'features/knowledgeDocumentViewer/utils/docReferenceResolver'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'
import {
  RolodexAdvancedFilterOperator,
  RolodexEntryType,
  RolodexFilterOp,
} from 'types'

export function useOffPageConnectorDocuments(
  searchText: string | string[] | undefined,
  siteID: string
) {
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()
  const loadOptions = useMemo(() => {
    const requested = (
      Array.isArray(searchText) ? searchText : [searchText]
    ).filter((text): text is string => !!text)
    // A drawing number is stored with whatever separator the title block used
    // (dots / hyphens / glued), while the connector prints its own form. Search
    // every separator variant so a dotted OPC still finds a hyphenated home.
    const searchTexts = [
      ...new Set(requested.flatMap(drawingNumberSearchVariants)),
    ]
    if (isPipelineConfigScopeLoading || searchTexts.length === 0)
      return undefined

    // One OR clause per (field, text): Rolodex's REGEX op matches regex
    // metacharacters literally, so alternation like `(a|b)` finds nothing.
    const escaped = searchTexts.map((text) =>
      text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    )
    const filters = escaped.flatMap((value) =>
      (['title', 'drawing_number', 'document_title'] as const).map((key) => ({
        field: 'data',
        key,
        op: RolodexFilterOp.REGEX,
        value,
      }))
    )

    const scopedTags = getPipelineConfigScopedTags({ site: siteID })
    if (!scopedTags) return undefined

    return {
      type: [RolodexEntryType.SYSTEM_DOCUMENT],
      tags: scopedTags,
      advanced_filter: {
        operator: RolodexAdvancedFilterOperator.OR,
        filters,
      },
      limit: 1000000,
    }
  }, [
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    searchText,
    siteID,
  ])

  const { data, isLoading, error } =
    useGetRolodexEntriesByLoadOptions<SystemDocumentEntry[]>(loadOptions)

  return {
    systemDocumentEntries: data ?? [],
    isLoading: isLoading || (!!loadOptions && data === undefined && !error),
    error,
  }
}
